import { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Button, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Snackbar, Alert,
} from '@mui/material';
import { People, DirectionsCar, Groups, Logout } from '@mui/icons-material';
import api from '../../api/client';
import { formatTime24, formatDateShort } from '../../utils/time';
import { OnlineUser, ShellOutletContext } from '../../utils/presence';
import { useAuth } from '../../contexts/AuthContext';

export default function WhoOnlinePage() {
  const { online } = useOutletContext<ShellOutletContext>();
  const { user } = useAuth();
  const isAdmin = !!user && (user.role === 'admin' || user.role === 'superadmin' || user.admin_access || user.superadmin_access);
  const isSuperadmin = !!user && (user.role === 'superadmin' || user.superadmin_access);
  const [rows, setRows] = useState<OnlineUser[]>([]);
  const [bootTarget, setBootTarget] = useState<OnlineUser | null>(null);
  const [booting, setBooting] = useState(false);
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const load = useCallback(() => {
    api.get('/online/detail').then((r) => setRows(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  // online counts change → refresh the detail list too
  useEffect(() => { load(); }, [online.total_users, load]);

  const confirmBoot = async () => {
    if (!bootTarget) return;
    setBooting(true);
    try {
      await api.post('/online/boot', { user_key: bootTarget.user_id });
      setSnack({ msg: `Session revoked for ${bootTarget.full_name || bootTarget.username}`, sev: 'success' });
      setBootTarget(null);
      load();
    } catch (e) {
      setSnack({ msg: 'Failed to revoke session', sev: 'error' });
    } finally {
      setBooting(false);
    }
  };

  const colCount = 5 + (isAdmin ? 1 : 0) + (isSuperadmin ? 1 : 0);

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Who's Online</Typography>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Stat icon={<People />} value={online.dispatch_users} label="Dispatch" />
        <Stat icon={<DirectionsCar />} value={online.mdt_users} label="MDT" />
        <Stat icon={<Groups />} value={online.total_users} label="Total" />
      </Stack>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Area</TableCell>
              {isAdmin && <TableCell>IP Address</TableCell>}
              <TableCell>Online Since</TableCell>
              {isSuperadmin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={colCount} sx={{ color: 'text.secondary' }}>No users online.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={`${r.user_id}-${r.area}`}>
                <TableCell>{r.full_name || r.username}</TableCell>
                <TableCell>{r.username}</TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>{r.role}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={r.area === 'mdt' ? 'MDT' : 'Dispatch'}
                    color={r.area === 'mdt' ? 'warning' : 'primary'}
                    variant="outlined"
                  />
                </TableCell>
                {isAdmin && (
                  <TableCell sx={{ fontFamily: 'monospace' }}>{r.ip_address || '—'}</TableCell>
                )}
                <TableCell>{r.since ? `${formatDateShort(r.since)} ${formatTime24(r.since)}` : '—'}</TableCell>
                {isSuperadmin && (
                  <TableCell align="right">
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<Logout />}
                      disabled={r.user_id === `u${user?.user_id}`}
                      onClick={() => setBootTarget(r)}
                    >
                      Boot
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!bootTarget} onClose={() => (booting ? null : setBootTarget(null))}>
        <DialogTitle>Revoke session?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will immediately log out <strong>{bootTarget?.full_name || bootTarget?.username}</strong> and
            invalidate their current session token. They will be returned to the login screen and must sign in again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBootTarget(null)} disabled={booting}>Cancel</Button>
          <Button onClick={confirmBoot} color="error" variant="contained" disabled={booting}>
            {booting ? 'Revoking…' : 'Revoke session'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.sev} onClose={() => setSnack(null)}>{snack.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

function Stat({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  return (
    <Paper sx={{ px: 3, py: 2, textAlign: 'center', minWidth: 110 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', color: '#64b5f6', mb: 0.5 }}>{icon}</Box>
      <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  );
}
